import {
  menu,
  popFilterableSimpleMenu,
  popupTargetFromElement,
} from '@blocksuite/affine-components/context-menu';
import {
  renderCachedUserAvatarUrl,
  revokeCachedUserAvatarObjectUrl,
  UserAvatarCacheProvider,
  type UserAvatarCacheService,
} from '@blocksuite/affine-shared/services';
import { SignalWatcher, WithDisposable } from '@blocksuite/global/lit';
import { DeleteIcon } from '@blocksuite/icons/lit';
import { ShadowlessElement } from '@blocksuite/std';
import { type ReadonlySignal, type Signal, signal } from '@preact/signals-core';
import { html, nothing, type TemplateResult } from 'lit';
import { property } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';

import type { Row } from '../../../../core/view-manager/row.js';
import type { GanttBarDecoration, GanttRowData } from '../../core/bar-model.js';
import {
  readProgress,
  type RowClassification,
  RowValidator,
} from '../../core/bar-model.js';
import {
  clearBarStateCells,
  readBarStateFromCells,
  runPositionPipeline,
} from '../../core/position-pipeline.js';
import { snapDate } from '../../core/snapping.js';
import { TimeAxisMath } from '../../core/time-axis-math.js';
import type { GanttZoomLevel } from '../../define.js';
import type { BarState } from '../../extensions/types.js';
import type { GanttSingleView } from '../../gantt-view-manager.js';
import {
  resolveBarChip,
  type ResolvedBarAssignee,
  type ResolvedBarChip,
} from '../cell-display.js';
import { DefaultLinkPort } from '../dependencies/default-link-port.js';
import type { LineEndpoints } from '../dependencies/dependency-line.js';
import {
  type LinkMagnetTarget,
  startLinkDragSession,
} from '../dependencies/link-drag-session.js';
import {
  barWithHotspotStyle,
  LINK_MAGNET_RADIUS_PX,
  linkArrivalStyle,
  linkHotspotStyle,
} from '../dependencies/link-hotspot-css.js';
import type { GanttLinkPort } from '../dependencies/link-port.js';
import { GanttDragTooltip } from '../drag/drag-tooltip.js';
import { detectDragZone } from '../drag/hit-zone.js';
import {
  CreateHandler,
  type DragHandler,
  MoveHandler,
  ResizeEndHandler,
  ResizeStartHandler,
  startDragSession,
} from '../drag/index.js';
import {
  badgeListStyle,
  BAR_HEIGHT,
  BAR_VERTICAL_PADDING,
  barChipAvatarStyle,
  barChipProgressStyle,
  barChipRowStyle,
  barChipSecondaryStyle,
  barChipStatusStyle,
  barChipStyle,
  barChipTitleStyle,
  barContainerStyle,
  barContentStyle,
  barPeekButtonStyle,
  barStyle,
  invalidRangeIconStyle,
  milestoneResizeHandleStyle,
  milestoneStyle,
  progressFillStyle,
} from './bar-css.js';
import { GanttBarHoverTooltip } from './bar-hover-tooltip.js';

export const ROW_HEIGHT = BAR_HEIGHT + BAR_VERTICAL_PADDING * 2;

type PreviewMap = ReadonlyMap<string, BarState>;

function renderAssigneeAvatar(
  member: ResolvedBarAssignee,
  avatarCache: UserAvatarCacheService | null
) {
  if (!member.avatarUrl) {
    return member.initials;
  }

  return renderCachedUserAvatarUrl({
    avatarCache,
    avatarUrl: member.avatarUrl,
    userId: member.id,
    render: (url, cached) =>
      html`<img
        src=${url}
        alt=""
        @load=${() => revokeCachedUserAvatarObjectUrl(url, cached)}
        @error=${() => revokeCachedUserAvatarObjectUrl(url, cached)}
      />`,
  });
}

/**
 * Renders the bar layer for the visible gantt rows. The renderer runs
 * every row through:
 *
 *   1. cell-read → raw `BarState`
 *   2. `PositionPipeline` (every registered transform, priority order)
 *   3. `RowValidator.classify` → decides bar / milestone / invalid
 *   4. `BarDecorator` hook pass
 *
 * During an active drag session the renderer overlays a preview entry
 * for the moving bar — that keeps the live position visible without
 * writing to Y.js every frame.
 *
 * Virtualisation arrives in sl-p9sb.5.1; for now the caller passes in
 * the already-filtered row list.
 */
export class GanttBarRenderer extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  /** Sentinel rowId used by drag-to-create before a real row exists. */
  static readonly CREATE_PREVIEW_ROW_ID = '__gantt-create-preview__';

  @property({ attribute: false })
  accessor view!: GanttSingleView;

  @property({ attribute: false })
  accessor zoom: GanttZoomLevel = 'week';

  @property({ attribute: false })
  accessor originMs!: number;

  @property({ attribute: false })
  accessor fromMs!: number;

  @property({ attribute: false })
  accessor onBarClick: ((rowId: string) => void) | undefined;

  /**
   * Optional row whitelist. When set, the renderer draws only these
   * rows (typically the window the virtual scroller emitted). When
   * `undefined`, it falls back to the full `view.rows$` list — that
   * keeps the renderer usable in non-virtual call-sites (empty-state,
   * tests).
   */
  @property({ attribute: false })
  accessor visibleRows: readonly Row[] | undefined = undefined;

  /** Optional index-offset applied to the row-to-y translation. */
  @property({ attribute: false })
  accessor rowIndexOffset = 0;

  /**
   * Persists dependency links. Consumers typically inject one wrapping
   * the upstream relation service; unset callers fall back to a cell
   * writer local to this view.
   */
  @property({ attribute: false })
  accessor linkPort: GanttLinkPort | undefined = undefined;

  /**
   * Surfaces the live link-drag endpoints (client-space) so the
   * dependency overlay can render the dashed arrow above bars.
   */
  @property({ attribute: false })
  accessor onLinkPreview:
    | ((endpoints: LineEndpoints | null) => void)
    | undefined;

  /**
   * Current multi-select state. Host owns the signal so the label
   * column stays in sync with the bar area. When >1 ids are present
   * and the user drags a bar whose rowId is a member, the renderer
   * group-moves every selected bar by the same delta.
   */
  @property({ attribute: false })
  accessor selectedRowIds: ReadonlySignal<ReadonlySet<string>> | undefined =
    undefined;

  /**
   * Invoked on plain or shift click. Shift+click toggles membership;
   * plain click collapses the set to a single rowId (handled by host).
   */
  @property({ attribute: false })
  accessor onSelectRow:
    | ((rowId: string, shiftKey: boolean) => void)
    | undefined;

  /**
   * Alt+drag on a bar triggers a row reorder instead of a date move.
   * The host owns the drop indicator + rowMove commit so this stays a
   * dumb bridge.
   */
  @property({ attribute: false })
  accessor onRowReorderStart:
    | ((evt: PointerEvent, rowId: string, captureTarget: HTMLElement) => void)
    | undefined;

  /**
   * Per-row vertical pixel offset while a reorder drag is live. Bars and
   * milestones apply this as `translateY` so they glide to their
   * post-drop positions in sync with the sticky label column. Empty map
   * (or missing rowId) means the row stays put.
   */
  @property({ attribute: false })
  accessor rowShifts: ReadonlyMap<string, number> = new Map();

  /**
   * Shared drag-preview signal. The bar renderer writes proposed
   * {@link BarState} snapshots here on every pointermove (per rowId)
   * and clears entries on commit/cancel. The dependency overlay reads
   * the same signal so arrows follow moving bars in real time. When
   * the host does not provide one, we fall back to a local signal so
   * standalone renderer call-sites (unit tests, empty-state) stay
   * self-sufficient.
   */
  @property({ attribute: false })
  accessor previewOverrides: Signal<PreviewMap> = signal(new Map());

  /**
   * Row currently magnetized during a link-drag. Written by the
   * renderer's own `resolveMagnet` callback and read back during
   * render to stamp `data-arrival-magnet` on the matching arrival
   * span. Null when no link-drag is active or the cursor is outside
   * the magnet radius of every visible bar.
   */
  @property({ attribute: false })
  accessor magnetRowId: Signal<string | null> = signal<string | null>(null);

  /**
   * Drag tooltip. Mounted under `document.body` rather than inside the
   * bar-renderer because Edgeless wraps the scene in a CSS
   * transform — `position: fixed` descendants bind to the transformed
   * ancestor instead of the viewport. Reparenting to body restores the
   * expected viewport-relative positioning so the tooltip tracks the
   * cursor correctly in both doc and edgeless modes.
   */
  private tooltipInstance: GanttDragTooltip | null = null;

  /**
   * Hover-tooltip (full-info card on bar rest). Lazily created on the
   * first `pointerenter` that survives the hover delay, reused for
   * the rest of the renderer's lifetime. Imperative lifecycle
   * mirrors the drag tooltip above — portal-mounted on body so
   * `position: fixed` resolves against the viewport even inside the
   * Edgeless CSS-transformed scene.
   */
  private hoverTooltipInstance: GanttBarHoverTooltip | null = null;
  private hoverTimer: ReturnType<typeof setTimeout> | null = null;
  private hoveredRowId: string | null = null;
  /** Delay before the hover card appears, ms. */
  private static readonly HOVER_DELAY_MS = 350;

  override connectedCallback(): void {
    super.connectedCallback();
    this.classList.add(barContainerStyle);
    this.addEventListener('pointerdown', this.handlePointerDown);
    this.addEventListener('contextmenu', this.handleContextMenu);
    this.addEventListener('pointerover', this.handlePointerOver);
    this.addEventListener('pointerout', this.handlePointerOut);
    this.addEventListener('pointermove', this.handleHoverPointerMove);
    const tip = document.createElement(
      'dv-gantt-drag-tooltip'
    ) as GanttDragTooltip;
    document.body.appendChild(tip);
    this.tooltipInstance = tip;
  }

  override disconnectedCallback(): void {
    this.removeEventListener('pointerdown', this.handlePointerDown);
    this.removeEventListener('contextmenu', this.handleContextMenu);
    this.removeEventListener('pointerover', this.handlePointerOver);
    this.removeEventListener('pointerout', this.handlePointerOut);
    this.removeEventListener('pointermove', this.handleHoverPointerMove);
    this.clearHoverTimer();
    this.tooltipInstance?.remove();
    this.tooltipInstance = null;
    this.hoverTooltipInstance?.remove();
    this.hoverTooltipInstance = null;
    super.disconnectedCallback();
  }

  override render(): TemplateResult {
    const rows = this.visibleRows ?? this.getRowsWindowSnapshot();
    const indexOffset = this.rowIndexOffset;
    const math = new TimeAxisMath(this.zoom, this.originMs);
    const transforms = this.view.extensions.positionTransforms.list();
    const decorators = this.view.extensions.barDecorators.list();
    const overrides = this.previewOverrides.value;

    const content = rows.map((row, localIndex) => {
      const rowIndex = localIndex + indexOffset;
      const override = overrides.get(row.rowId);
      const rawState = override ?? readBarStateFromCells(this.view, row);
      const data: GanttRowData = {
        row,
        rowIndex,
        state: rawState
          ? runPositionPipeline(rawState, transforms, { view: this.view, row })
          : null,
        classification: RowValidator.classify(rawState),
        progress: readProgress(this.view, row),
      };

      if (data.classification === 'unscheduled' || !data.state) return nothing;

      const decorations: GanttBarDecoration[] = [];
      for (const decorator of decorators) {
        const raw = decorator.decorate(
          {
            rowId: data.row.rowId,
            startMs: data.state.startMs,
            endMs: data.state.endMs,
            x: math.dateToX(data.state.startMs) - math.dateToX(this.fromMs),
            y: data.rowIndex * ROW_HEIGHT + BAR_VERTICAL_PADDING,
            width: Math.max(
              2,
              math.dateToX(data.state.endMs) - math.dateToX(data.state.startMs)
            ),
            height: BAR_HEIGHT,
          },
          data.row,
          this.view
        );
        if (raw) decorations.push({ badges: [String(raw)] });
      }

      return this.renderRow(data, math, decorations);
    });

    const createPreview = overrides.get(GanttBarRenderer.CREATE_PREVIEW_ROW_ID);
    const createPreviewBar = createPreview
      ? this.renderCreatePreview(createPreview, math, rows.length + indexOffset)
      : nothing;

    return html` ${content} ${createPreviewBar} `;
  }

  /**
   * Debounced hover intent. Setting `hoveredRowId` + starting a
   * timer lets the user glide the pointer across rows without the
   * tooltip flashing through every bar — only a sustained rest on
   * one bar reveals the card.
   */
  private readonly handlePointerOver = (evt: PointerEvent): void => {
    const barEl = (evt.target as HTMLElement | null)?.closest<HTMLElement>(
      '[data-bar-row-id]'
    );
    if (!barEl) return;
    const rowId = barEl.dataset.barRowId;
    if (!rowId || rowId === this.hoveredRowId) return;
    this.hoveredRowId = rowId;
    this.clearHoverTimer();
    // Capture client coords now; if the pointer moves before the
    // delay elapses the move handler keeps them fresh.
    const clientX = evt.clientX;
    const clientY = evt.clientY;
    this.hoverTimer = setTimeout(() => {
      this.hoverTimer = null;
      this.showHoverTooltip(rowId, clientX, clientY);
    }, GanttBarRenderer.HOVER_DELAY_MS);
  };

  private readonly handlePointerOut = (evt: PointerEvent): void => {
    const barEl = (evt.target as HTMLElement | null)?.closest<HTMLElement>(
      '[data-bar-row-id]'
    );
    if (!barEl) return;
    // `pointerout` fires for every child transition; only act when
    // the pointer truly leaves the bar (its `relatedTarget` is not
    // inside it). Otherwise hovering a child (e.g. the peek button)
    // would collapse the card.
    const next = evt.relatedTarget as HTMLElement | null;
    if (next && barEl.contains(next)) return;
    this.clearHoverTimer();
    this.hoveredRowId = null;
    this.hoverTooltipInstance?.hide();
  };

  private readonly handleHoverPointerMove = (evt: PointerEvent): void => {
    if (!this.hoverTooltipInstance?.visible) return;
    // Track the cursor while the card is open so it glides with
    // the user — feels responsive on wide bars where the rest
    // position can drift by several hundred pixels.
    this.hoverTooltipInstance.clientX = evt.clientX;
    this.hoverTooltipInstance.clientY = evt.clientY;
  };

  private showHoverTooltip(rowId: string, x: number, y: number): void {
    if (this.view.readonly$.value && !this.hoverTooltipInstance) {
      // Readonly gantt still wants the hover card — it's read-only
      // itself. The gate is here only to defer the lazy element
      // creation on the first pointer in both modes.
    }
    if (!this.hoverTooltipInstance) {
      const tip = document.createElement(
        'dv-gantt-bar-hover-tooltip'
      ) as GanttBarHoverTooltip;
      document.body.appendChild(tip);
      this.hoverTooltipInstance = tip;
    }
    this.hoverTooltipInstance.show({
      clientX: x,
      clientY: y,
      view: this.view,
      rowId,
    });
  }

  private clearHoverTimer(): void {
    if (this.hoverTimer != null) {
      clearTimeout(this.hoverTimer);
      this.hoverTimer = null;
    }
  }

  private readonly handlePointerDown = (evt: PointerEvent): void => {
    // Any interaction tears down the hover card; we don't want it
    // lingering over an active drag / selection.
    this.clearHoverTimer();
    this.hoverTooltipInstance?.hide();
    if (this.view.readonly$.value) return;
    if (evt.button !== 0 || !evt.isPrimary) return;

    const target = evt.target as HTMLElement | null;
    if (!target) return;

    const hotspotEl = target.closest<HTMLElement>('[data-link-hotspot]');
    if (hotspotEl) {
      const rowId = hotspotEl.dataset.rowId;
      if (rowId) this.startLinkDrag(evt, rowId, hotspotEl);
      return;
    }

    const barEl = target.closest<HTMLElement>('[data-bar-row-id]');
    if (barEl) {
      this.startBarDrag(evt, barEl);
      return;
    }

    // Empty timeline area. Resolve the row whose vertical band the
    // pointer is in — if it exists AND has no bar (unscheduled), the
    // drag schedules that row in place. Below the last row, fall
    // through to the legacy "create a brand-new row" path.
    const emptyRow = this.resolveRowAtPointer(evt.clientY);
    if (emptyRow) {
      this.startScheduleDrag(evt, emptyRow);
      return;
    }

    this.startCreateDrag(evt);
  };

  /**
   * Right-click on a bar opens a one-item context menu offering to
   * unschedule the bar(s). Row deletion lives on the label-column
   * selection path (Table/Kanban parity: you delete a row by
   * selecting it as a row, not via a date-derived visualisation).
   *
   * If the clicked bar is already in the multi-selection, the action
   * targets every selected bar. Otherwise the selection collapses to
   * the clicked bar first so "right-click → Delete bar" always
   * matches the user's target rather than a stale prior selection.
   */
  private readonly handleContextMenu = (evt: MouseEvent): void => {
    if (this.view.readonly$.value) return;
    const target = evt.target as HTMLElement | null;
    const barEl = target?.closest<HTMLElement>('[data-bar-row-id]');
    if (!barEl) return;
    const rowId = barEl.dataset.barRowId;
    if (!rowId) return;

    evt.preventDefault();
    evt.stopPropagation();

    const current = this.selectedRowIds?.value;
    const rightClickInSelection = current?.has(rowId) ?? false;
    const ids = rightClickInSelection ? [...current!] : [rowId];
    if (!rightClickInSelection) this.onSelectRow?.(rowId, false);

    const label = ids.length > 1 ? `Delete ${ids.length} bars` : 'Delete bar';
    popFilterableSimpleMenu(popupTargetFromElement(barEl), [
      menu.action({
        name: label,
        prefix: DeleteIcon(),
        select: () => {
          clearBarStateCells(this.view, ids);
        },
      }),
    ]);
  };

  /**
   * Resolve the existing row (if any) whose y-band contains `clientY`
   * AND which is currently unscheduled. Returns `null` when the pointer
   * is outside every row or when the hit row already has a bar (that
   * case is handled by {@link startBarDrag} via `data-bar-row-id`).
   */
  private resolveRowAtPointer(clientY: number): string | null {
    const y = this.clientToLocalY(clientY) + this.rowIndexOffset * ROW_HEIGHT;
    const index = Math.floor(y / ROW_HEIGHT);
    if (index < 0) return null;
    const rows = this.getRowsWindowSnapshot();
    const row = rows[index];
    if (!row) return null;
    const existing = readBarStateFromCells(this.view, row);
    if (existing) return null;
    return row.rowId;
  }

  private startLinkDrag(
    evt: PointerEvent,
    fromRowId: string,
    hotspotEl: HTMLElement
  ): void {
    const rows = this.getRowsWindowSnapshot();
    const row = rows.find(r => r.rowId === fromRowId);
    if (!row) return;
    const fromBarState = readBarStateFromCells(this.view, row);
    if (!fromBarState) return;

    const rect = hotspotEl.getBoundingClientRect();
    const port = this.linkPort ?? new DefaultLinkPort(this.view);

    // Geometric magnet: iterate visible bars, compute the client-space
    // center of each arrival handle (= the bar's left edge midline),
    // pick the nearest within LINK_MAGNET_RADIUS_PX. Pure math — no
    // DOM lookups — so it stays cheap at high pointermove rates and
    // works even for bars that happen to be clipped under a sticky
    // label column. Self-targets are excluded so users never see the
    // source bar's own handle pull on its outgoing drag.
    const resolveMagnet = (
      clientX: number,
      clientY: number
    ): LinkMagnetTarget | null => {
      const hostRect = this.getBoundingClientRect();
      const scale = this.getHostScale();
      const math = new TimeAxisMath(this.zoom, this.originMs);
      const fromOffset = math.dateToX(this.fromMs);
      const rows = this.visibleRows ?? this.getRowsWindowSnapshot();
      const rowIndexOffset = this.rowIndexOffset;
      const radiusSq = LINK_MAGNET_RADIUS_PX * LINK_MAGNET_RADIUS_PX;

      let bestRowId: string | null = null;
      let bestDistSq = radiusSq;
      let bestX = 0;
      let bestY = 0;

      for (let i = 0; i < rows.length; i++) {
        const row = rows[i]!;
        if (row.rowId === fromRowId) continue;
        const state = readBarStateFromCells(this.view, row);
        if (!state) continue;
        const rowIndex = i + rowIndexOffset;
        const arrivalX =
          hostRect.left + (math.dateToX(state.startMs) - fromOffset) * scale.x;
        const arrivalY =
          hostRect.top +
          (rowIndex * ROW_HEIGHT + BAR_VERTICAL_PADDING + BAR_HEIGHT / 2) *
            scale.y;
        const dx = clientX - arrivalX;
        const dy = clientY - arrivalY;
        const distSq = dx * dx + dy * dy;
        if (distSq < bestDistSq) {
          bestDistSq = distSq;
          bestRowId = row.rowId;
          bestX = arrivalX;
          bestY = arrivalY;
        }
      }

      // Update the shared signal on every call so the bar renderer
      // can re-render with the magnet data attribute applied — `null`
      // when the cursor leaves every handle's pull zone.
      if (this.magnetRowId.value !== bestRowId) {
        this.magnetRowId.value = bestRowId;
      }
      if (!bestRowId) return null;
      return { rowId: bestRowId, clientX: bestX, clientY: bestY };
    };

    startLinkDragSession({
      view: this.view,
      target: this,
      pointerEvent: evt,
      fromRowId,
      fromBarState,
      originPx: {
        clientX: rect.left + rect.width / 2,
        clientY: rect.top + rect.height / 2,
      },
      resolveRowAt: (x, y) => {
        const els =
          typeof document !== 'undefined'
            ? document.elementsFromPoint(x, y)
            : [];
        for (const el of els) {
          if (!(el instanceof HTMLElement)) continue;
          const barAt = el.closest<HTMLElement>('[data-bar-row-id]');
          if (barAt?.dataset.barRowId) return barAt.dataset.barRowId;
        }
        return null;
      },
      resolveMagnet,
      port,
      onPreview: endpoints => {
        // Drag ended — clear the magnet so the arrival handles
        // snap back to their idle size + hidden state.
        if (!endpoints) this.magnetRowId.value = null;
        this.onLinkPreview?.(endpoints);
      },
    });
  }

  private startBarDrag(evt: PointerEvent, barEl: HTMLElement): void {
    const rowId = barEl.dataset.barRowId;
    if (!rowId) return;
    const row = this.getRowById(rowId);
    if (!row) return;
    const initial = readBarStateFromCells(this.view, row);
    if (!initial) return;

    // Shift+click toggles multi-selection and suppresses the drag —
    // the user is building the set, not moving anything yet.
    if (evt.shiftKey) {
      this.onSelectRow?.(rowId, true);
      evt.preventDefault();
      evt.stopPropagation();
      return;
    }

    // Plain interaction: if the clicked bar is not already part of the
    // multi-selection, collapse the set to this single row so the
    // subsequent drag moves only one bar.
    const currentSel = this.selectedRowIds?.value;
    if (!currentSel || !currentSel.has(rowId)) {
      this.onSelectRow?.(rowId, false);
    }

    const rect = barEl.getBoundingClientRect();
    const forceReorder = evt.altKey;
    const resizeEdge = (evt.target as HTMLElement | null)?.closest<HTMLElement>(
      '[data-resize-edge]'
    )?.dataset.resizeEdge;
    const initialZone = detectDragZone(
      evt.clientX - rect.left,
      rect.width,
      resizeEdge === 'left-edge' || resizeEdge === 'right-edge'
        ? resizeEdge
        : undefined
    );
    const onEdge = initialZone === 'left-edge' || initialZone === 'right-edge';

    // Direction-deferred drag: horizontal-dominant → date move/resize
    // via DragSession. Vertical-dominant → row reorder. Alt always
    // forces reorder regardless of direction. Edge zones (resize
    // handles) skip direction detection — users grabbing the edge
    // always mean "resize", even with a slightly wobbly pointer. The
    // decision happens on the first pointer movement past a 4 px
    // deadzone so a still-click still fires `onBarClick`.
    const startX = evt.clientX;
    const startY = evt.clientY;
    let decided = false;
    try {
      barEl.setPointerCapture(evt.pointerId);
    } catch {
      // Capture may be unavailable if the element detaches mid-event.
    }

    const beginHorizontalDateDrag = (): void => {
      const zone = initialZone;
      let handler: DragHandler;
      if (zone === 'left-edge') handler = new ResizeStartHandler(initial);
      else if (zone === 'right-edge') handler = new ResizeEndHandler(initial);
      else handler = new MoveHandler(initial);

      const activeSel = this.selectedRowIds?.value ?? new Set<string>();
      const groupInitials = new Map<string, BarState>();
      if (
        handler instanceof MoveHandler &&
        activeSel.size > 1 &&
        activeSel.has(rowId)
      ) {
        for (const id of activeSel) {
          if (id === rowId) continue;
          const r = this.getRowById(id);
          if (!r) continue;
          const s = readBarStateFromCells(this.view, r);
          if (s) groupInitials.set(id, s);
        }
      }

      // Hand pointer over to DragSession. It re-captures and installs
      // its own listeners, so we must release ours first.
      cleanupInitListeners();
      try {
        barEl.releasePointerCapture(evt.pointerId);
      } catch {
        // Capture may have been lost.
      }
      this.beginSession(
        evt,
        barEl,
        handler,
        rowId,
        // No still-click → detail panel. Detail panel is reached
        // only through the peek icon rendered on the bar.
        undefined,
        groupInitials
      );
    };

    const beginReorder = (triggerEvt: PointerEvent): void => {
      cleanupInitListeners();
      try {
        barEl.releasePointerCapture(evt.pointerId);
      } catch {
        // Capture may have been lost.
      }
      this.onRowReorderStart?.(triggerEvt, rowId, barEl);
    };

    const cleanupInitListeners = (): void => {
      barEl.removeEventListener('pointermove', onInitMove);
      barEl.removeEventListener('pointerup', onInitUp);
      barEl.removeEventListener('pointercancel', onInitUp);
    };

    const onInitMove = (move: PointerEvent): void => {
      if (decided) return;
      const dx = move.clientX - startX;
      const dy = move.clientY - startY;
      if (Math.hypot(dx, dy) < 4) return;
      decided = true;
      const isVertical =
        forceReorder || (!onEdge && Math.abs(dy) > Math.abs(dx) * 1.2);
      if (isVertical) beginReorder(move);
      else beginHorizontalDateDrag();
    };

    const onInitUp = (): void => {
      cleanupInitListeners();
      try {
        barEl.releasePointerCapture(evt.pointerId);
      } catch {
        // Capture may have been lost.
      }
      // Bar body click = select (already done at pointerdown via
      // `onSelectRow`). The detail panel is opened ONLY via the peek
      // icon on the bar — matches the Kanban card pattern and
      // matches users' expectation that clicking the body of a row
      // doesn't immediately pop a dialog.
    };

    barEl.addEventListener('pointermove', onInitMove);
    barEl.addEventListener('pointerup', onInitUp);
    barEl.addEventListener('pointercancel', onInitUp);
    evt.preventDefault();
    evt.stopPropagation();
  }

  private startCreateDrag(evt: PointerEvent): void {
    const anchorMs = this.anchorMsFromPointer(evt);
    const handler = new CreateHandler(
      GanttBarRenderer.CREATE_PREVIEW_ROW_ID,
      anchorMs
    );
    this.beginSession(
      evt,
      this,
      handler,
      GanttBarRenderer.CREATE_PREVIEW_ROW_ID,
      undefined
    );
  }

  private getRowsWindowSnapshot(): Row[] {
    return this.view.getBoundedRowsWindow().rows;
  }

  private getRowById(rowId: string): Row | undefined {
    return this.getRowsWindowSnapshot().find(row => row.rowId === rowId);
  }

  /**
   * Schedule an existing (unscheduled) row. Two paths: drag creates a
   * bar of the dragged range (same mechanics as {@link startCreateDrag}
   * but commits to the known `rowId`); a pure click (no drag past the
   * {@link CLICK_THRESHOLD_PX}) commits a default 3-day bar anchored
   * at the click point. The click path uses `beginSession`'s `onClick`
   * callback so the DragSession lifecycle still runs (pointer capture,
   * stop-propagation against Edgeless pan), just without a preview.
   */
  private startScheduleDrag(evt: PointerEvent, rowId: string): void {
    const anchorMs = this.anchorMsFromPointer(evt);
    const handler = new CreateHandler(rowId, anchorMs);
    const onClick = (): void => this.commitClickCreate(rowId, anchorMs);
    this.beginSession(evt, this, handler, rowId, onClick, undefined, {
      commitTo: rowId,
    });
  }

  /**
   * Create a default-span bar on a still-click. Duration is a short
   * fixed 3 days — long enough to be clearly a bar at every zoom, short
   * enough that the user still resizes it to the real range. Honours
   * the view's snap mode via {@link anchorMsFromPointer} (which already
   * snapped the anchor) so drag + click behave identically up to the
   * duration.
   */
  private commitClickCreate(rowId: string, anchorMs: number): void {
    const startCol = this.view.startDateCol$.value;
    const endCol = this.view.endDateCol$.value;
    if (!startCol || !endCol) return;
    const DAY_MS = 24 * 60 * 60 * 1000;
    const endMs = anchorMs + 3 * DAY_MS;
    this.view.propertyGetOrCreate(startCol).valueSet(rowId, anchorMs);
    this.view.propertyGetOrCreate(endCol).valueSet(rowId, endMs);
  }

  private anchorMsFromPointer(evt: PointerEvent): number {
    const math = new TimeAxisMath(this.zoom, this.originMs);
    const offsetPx =
      this.clientToLocalX(evt.clientX) + math.dateToX(this.fromMs);
    return snapDate(math.xToDate(offsetPx), this.view.snapMode$.value);
  }

  private getHostScale(): { x: number; y: number } {
    const rect = this.getBoundingClientRect();
    const localWidth = this.offsetWidth || this.clientWidth || rect.width;
    const localHeight = this.offsetHeight || this.clientHeight || rect.height;
    const x = localWidth > 0 ? rect.width / localWidth : 1;
    const y = localHeight > 0 ? rect.height / localHeight : 1;
    return {
      x: Number.isFinite(x) && x > 0 ? x : 1,
      y: Number.isFinite(y) && y > 0 ? y : 1,
    };
  }

  private clientToLocalX(clientX: number): number {
    const rect = this.getBoundingClientRect();
    return (clientX - rect.left) / this.getHostScale().x;
  }

  private clientToLocalY(clientY: number): number {
    const rect = this.getBoundingClientRect();
    return (clientY - rect.top) / this.getHostScale().y;
  }

  private clientDeltaToLocalDeltaX(clientDeltaX: number): number {
    return clientDeltaX / this.getHostScale().x;
  }

  private beginSession(
    evt: PointerEvent,
    target: HTMLElement,
    handler: DragHandler,
    previewKey: string,
    onClick: (() => void) | undefined,
    groupInitials?: ReadonlyMap<string, BarState>,
    // `opts.commitTo` re-targets the commit at an existing row id —
    // used by the schedule-unscheduled-row path so the default `create`
    // intent doesn't call `rowAdd`. Without it, the session falls back
    // to `defaultCommit` which creates a new row on `create` intent.
    opts?: { commitTo?: string }
  ): void {
    const math = new TimeAxisMath(this.zoom, this.originMs);
    const hasGroup = !!groupInitials && groupInitials.size > 0;
    const baselineStartMs = handler.initialState.startMs;
    const commitTo = opts?.commitTo;

    // DragSession tears itself down on pointerup/cancel, so no handle
    // is kept here — the session owns its own lifecycle.
    startDragSession({
      view: this.view,
      math,
      handler,
      tooltip: this.tooltipInstance ?? undefined,
      target,
      pointerEvent: evt,
      clientDeltaToTimelineDelta: delta => this.clientDeltaToLocalDeltaX(delta),
      onClick,
      onPreview: state => {
        this.applyPreview(previewKey, state);
        if (!hasGroup || !groupInitials) return;
        if (!state) {
          // Rejected proposal — clear every group preview too.
          for (const [id] of groupInitials) this.applyPreview(id, null);
          return;
        }
        const delta = state.startMs - baselineStartMs;
        for (const [id, init] of groupInitials) {
          this.applyPreview(id, {
            ...init,
            startMs: init.startMs + delta,
            endMs: init.endMs + delta,
          });
        }
      },
      commit: hasGroup
        ? finalState => {
            const startCol = this.view.startDateCol$.value;
            const endCol = this.view.endDateCol$.value;
            if (!startCol || !endCol) return;
            const startProp = this.view.propertyGetOrCreate(startCol);
            const endProp = this.view.propertyGetOrCreate(endCol);
            startProp.valueSet(previewKey, finalState.startMs);
            endProp.valueSet(previewKey, finalState.endMs);
            const delta = finalState.startMs - baselineStartMs;
            if (!groupInitials) return;
            for (const [id, init] of groupInitials) {
              startProp.valueSet(id, init.startMs + delta);
              endProp.valueSet(id, init.endMs + delta);
            }
          }
        : commitTo
          ? finalState => {
              const startCol = this.view.startDateCol$.value;
              const endCol = this.view.endDateCol$.value;
              if (!startCol || !endCol) return;
              this.view
                .propertyGetOrCreate(startCol)
                .valueSet(commitTo, finalState.startMs);
              this.view
                .propertyGetOrCreate(endCol)
                .valueSet(commitTo, finalState.endMs);
            }
          : undefined,
    });
  }

  private applyPreview(previewKey: string, state: BarState | null): void {
    const next = new Map(this.previewOverrides.value);
    if (state) {
      next.set(previewKey, state);
    } else {
      next.delete(previewKey);
    }
    this.previewOverrides.value = next;
  }

  private renderRow(
    data: GanttRowData,
    math: TimeAxisMath,
    decorations: GanttBarDecoration[]
  ): TemplateResult {
    const state = data.state!;
    const x = math.dateToX(state.startMs) - math.dateToX(this.fromMs);
    const y = data.rowIndex * ROW_HEIGHT + BAR_VERTICAL_PADDING;

    if (state.startMs === state.endMs) {
      return this.renderMilestone(data, x, y);
    }

    const width = Math.max(
      2,
      math.dateToX(state.endMs) - math.dateToX(state.startMs)
    );

    const inlineStyles = decorations.reduce<Record<string, string>>(
      (acc, d) => ({ ...acc, ...(d.inlineStyles ?? {}) }),
      {}
    );
    const extraClasses = decorations.flatMap(d => d.classList ?? []);
    const badges = decorations.flatMap(d => d.badges ?? []);

    const chips = this.resolveBarChips(data.row.rowId);
    const selected = this.selectedRowIds?.value?.has(data.row.rowId) ?? false;
    const shiftY = this.rowShifts.get(data.row.rowId) ?? 0;
    // Status-option colour tints the bar's background when a status
    // column is wired. When no status column / option colour is set,
    // leave the background unset so barStyle's `&&&[data-classification]`
    // rule paints the theme primary. Invalid-range bars opt out of
    // tinting — the classification selector already paints them.
    const statusBg = this.resolveBarStatusColor(data.row.rowId);

    return html`
      <div
        class=${[barStyle, barWithHotspotStyle, ...extraClasses].join(' ')}
        data-bar-row-id=${data.row.rowId}
        data-classification=${data.classification}
        data-start-ms=${String(state.startMs)}
        data-end-ms=${String(state.endMs)}
        ?data-selected=${selected}
        style=${styleMap({
          left: `${x}px`,
          top: `${y}px`,
          width: `${width}px`,
          transform: shiftY !== 0 ? `translateY(${shiftY}px)` : undefined,
          transition:
            this.rowShifts.size > 0 ? 'transform 120ms ease-out' : 'none',
          willChange: 'transform',
          ...(statusBg && data.classification !== 'invalid-range'
            ? { background: statusBg }
            : {}),
          ...inlineStyles,
        })}
      >
        <div class=${barContentStyle}>
          ${this.renderProgressFill(data, width)}
          ${this.renderClassificationIndicator(data.classification)}
          <span class=${barChipRowStyle}>
            ${chips.map(chip => this.renderChip(chip))}
          </span>
          ${badges.length
            ? html`<span class=${badgeListStyle}>${badges.join(' ')}</span>`
            : nothing}
        </div>
        <button
          class=${barPeekButtonStyle}
          data-classification=${data.classification}
          aria-label="Open row detail"
          title="Open detail"
          style="color: var(--streamlain-v2-icon-primary);"
          @click=${(e: MouseEvent) => {
            e.stopPropagation();
            this.onBarClick?.(data.row.rowId);
          }}
        >
          <svg
            viewBox="0 0 16 16"
            fill="var(--streamlain-v2-icon-primary)"
            aria-hidden="true"
          >
            <path
              d="M4 4h3V2H2v5h2V4zm8 8h-3v2h5V9h-2v3zM14 2H9v2h3v3h2V2zM2 9v5h5v-2H4V9H2z"
            ></path>
          </svg>
        </button>
        <span
          class=${linkArrivalStyle}
          aria-hidden="true"
          ?data-arrival-magnet=${this.magnetRowId.value === data.row.rowId}
        ></span>
        <span
          class=${linkHotspotStyle}
          data-link-hotspot
          data-row-id=${data.row.rowId}
          aria-label="Link dependency"
        ></span>
      </div>
    `;
  }

  private renderMilestone(
    data: GanttRowData,
    x: number,
    y: number
  ): TemplateResult {
    const selected = this.selectedRowIds?.value?.has(data.row.rowId) ?? false;
    const shiftY = this.rowShifts.get(data.row.rowId) ?? 0;
    return html`
      <div
        class=${milestoneStyle}
        data-bar-row-id=${data.row.rowId}
        data-classification=${data.classification}
        data-start-ms=${String(data.state!.startMs)}
        data-end-ms=${String(data.state!.endMs)}
        ?data-selected=${selected}
        style=${styleMap({
          left: `${x - BAR_HEIGHT / 2}px`,
          top: `${y}px`,
          transform: shiftY !== 0 ? `translateY(${shiftY}px)` : undefined,
          transition:
            this.rowShifts.size > 0 ? 'transform 120ms ease-out' : 'none',
          willChange: 'transform',
        })}
      >
        <span
          class=${milestoneResizeHandleStyle}
          data-resize-edge="left-edge"
          aria-label="Resize start"
          title="Resize start"
        ></span>
        <span
          class=${milestoneResizeHandleStyle}
          data-resize-edge="right-edge"
          aria-label="Resize end"
          title="Resize end"
        ></span>
      </div>
    `;
  }

  private renderCreatePreview(
    state: BarState,
    math: TimeAxisMath,
    rowCount: number
  ): TemplateResult {
    const x = math.dateToX(state.startMs) - math.dateToX(this.fromMs);
    const width = Math.max(
      2,
      math.dateToX(state.endMs) - math.dateToX(state.startMs)
    );
    const y = rowCount * ROW_HEIGHT + BAR_VERTICAL_PADDING;
    return html`
      <div
        class=${barStyle}
        data-classification="create-preview"
        style=${styleMap({
          left: `${x}px`,
          top: `${y}px`,
          width: `${width}px`,
          opacity: '0.6',
        })}
      ></div>
    `;
  }

  /**
   * Read the status column's select-option colour for a row. Returns
   * `null` when the view has no status column, the cell is empty, or
   * the picked option has no colour assigned.
   *
   * Select cells store the picked option's id as the cell's raw value;
   * option metadata (including `color`) lives on the column property's
   * own `data$.value.options` list; `option.color` is the select palette.
   */
  private resolveBarStatusColor(rowId: string): string | null {
    const colId = this.view.statusCol$.value;
    if (!colId) return null;
    const optionId = this.view.cellGetOrCreate(rowId, colId).value$.value;
    if (typeof optionId !== 'string' || !optionId) return null;
    const propData = this.view.propertyGetOrCreate(colId).data$.value as
      | { options?: ReadonlyArray<{ id: string; color?: string }> }
      | null
      | undefined;
    const options = propData?.options;
    if (!options) return null;
    const match = options.find(o => o.id === optionId);
    const raw = match?.color;
    return raw ?? null;
  }

  /**
   * Walk the user's ordered display items and produce the subset of
   * resolvable chips for a row. Items whose role column is unset, or
   * whose cell is empty, drop out silently — an empty chip would
   * just waste horizontal space on the bar.
   */
  private resolveBarChips(rowId: string): ResolvedBarChip[] {
    const items = this.view.barDisplayItems$.value;
    const out: ResolvedBarChip[] = [];
    for (const item of items) {
      if (!item.visible) continue;
      const chip = resolveBarChip(this.view, rowId, item.kind, item.variant);
      if (chip) out.push(chip);
    }
    return out;
  }

  private renderChip(chip: ResolvedBarChip): TemplateResult {
    const isTitle = chip.kind === 'title';
    const base = `${barChipStyle} ${isTitle ? barChipTitleStyle : barChipSecondaryStyle}`;

    if (chip.kind === 'assignee') {
      // Avatar slot shows the first member's photo when known, with
      // initials as the in-place fallback so empty-avatar users never
      // leave the chip anchorless. Multi-assignee rows still show a
      // single avatar (the first member) — the name list beside it
      // carries the full attribution. Matches how the table member
      // cell composes a preview row: avatar(first) + "A, B, C".
      const first = chip.members?.[0];
      const avatarCache = this.view.serviceGet(UserAvatarCacheProvider) ?? null;
      const avatar = chip.avatarInitials
        ? html`<span class=${barChipAvatarStyle}>
            ${first
              ? renderAssigneeAvatar(first, avatarCache)
              : chip.avatarInitials}
          </span>`
        : nothing;
      const name = chip.text
        ? html`<span style="overflow:hidden;text-overflow:ellipsis;"
            >${chip.text}</span
          >`
        : nothing;
      return html`<span class=${base}>${avatar}${name}</span>`;
    }

    if (chip.kind === 'status') {
      const tint = chip.statusColor;
      // When the select option has its own colour, tint the pill
      // inline — avoids needing a new CSS class per option.
      const style = tint
        ? `background: color-mix(in srgb, ${tint} 45%, transparent); color: inherit;`
        : undefined;
      return html`<span
        class=${`${base} ${barChipStatusStyle}`}
        style=${style ?? nothing}
        >${chip.text}</span
      >`;
    }

    if (chip.kind === 'progress') {
      return html`<span class=${`${base} ${barChipProgressStyle}`}
        >${chip.text}</span
      >`;
    }

    return html`<span class=${base}>${chip.text}</span>`;
  }

  private renderProgressFill(
    data: GanttRowData,
    barWidth: number
  ): TemplateResult | typeof nothing {
    if (data.progress == null) return nothing;
    return html`
      <div
        class=${progressFillStyle}
        style=${styleMap({ width: `${barWidth * data.progress}px` })}
      ></div>
    `;
  }

  private renderClassificationIndicator(
    classification: RowClassification
  ): TemplateResult | typeof nothing {
    if (classification !== 'invalid-range') return nothing;
    return html`<span class=${invalidRangeIconStyle}>!</span>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'dv-gantt-bar-renderer': GanttBarRenderer;
  }
}

/**
 * Row type re-export — keeps bar-renderer the single import surface for
 * consumers building atop its rendering model.
 */
export type { Row };
